import { formatDateDisplay } from '../utils/date';
import { LEGAL_ENTITY_NAME } from '../config/brandProfiles';

function Section({ number, title, children }: { number: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="flex items-baseline gap-3 text-lg font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
        <span className="text-blue-600 font-bold tabular-nums">{number}.</span>
        {title}
      </h2>
      <div className="space-y-3 text-sm text-gray-700 leading-relaxed">{children}</div>
    </section>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-5">
      <h3 className="text-sm font-semibold text-gray-900 mb-1.5">{title}</h3>
      <p>{children}</p>
    </div>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="mt-2 space-y-1.5 list-none">
      {items.map((item) => (
        <li key={item} className="flex gap-2">
          <span className="mt-0.5 text-blue-400 shrink-0">&#8227;</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="mb-10 text-center">
          <p className="text-xl font-bold text-gray-900 mb-4">{LEGAL_ENTITY_NAME}</p>
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Privacy Policy</h1>
          <p className="text-sm text-gray-500">Last updated: {formatDateDisplay('2026-09-16')}</p>
        </div>

        {/* Intro card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-10 text-sm text-blue-900 leading-relaxed">
          Bright Minds Collective LLC ("we," "us," "our"), which operates Bright Horizon Chinese
          School ("BHCS") and Kaleido, respects your privacy. This Privacy Policy explains how we
          collect, use, and safeguard your information when you use our parent portal (the
          "Portal") for either program. Please read it carefully. By creating an account or using the Portal, you agree to
          the practices described here.
        </div>

        {/* Body */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-8 py-10">

          {/* ── 1. Information We Collect ───────────────────────────── */}
          <Section number="1" title="Information We Collect">
            <SubSection title="1.1 — Account and Profile Information">
              When you register, we collect your name, email address, phone number, and the
              information you provide about the students you enroll (such as name, date of birth,
              grade, and school, as applicable).
            </SubSection>

            <SubSection title="1.2 — Google Sign-In">
              When you sign in with Google, we receive your name, email address, and profile
              picture from your Google account (subject to your Google privacy settings). We use
              this information solely to create and maintain your Portal account.
            </SubSection>

            <SubSection title="1.3 — Payment Information">
              We collect payment details necessary to process transactions. All card and ACH
              payment data is handled by Stripe; we do not store complete credit card or banking
              information on our servers. Zelle payments are made directly through your bank and
              are not processed on the Portal.
            </SubSection>

            <SubSection title="1.4 — BHCS Cash Balance">
              If you use the BHCS Cash prepaid balance feature, we store your balance history,
              deposit records, and transaction activity. See our{' '}
              <a href="/terms" className="text-blue-700 hover:underline">Terms of Service</a>,
              Section 4, for full details on BHCS Cash.
            </SubSection>

            <SubSection title="1.5 — Student Headshot Photos">
              We may keep a single headshot photo of an enrolled student so that staff can confirm
              they are releasing the right child at pickup — for example when a substitute teacher,
              a merged camp roster, or the first days of a term mean the adult on duty does not
              personally know every student. Photos are collected by our office staff and uploaded
              by administrators; parents are not asked to upload them through the Portal. You may
              decline a headshot for your child, or ask us to remove one already on file, at any
              time by contacting us — participation in any program is never conditioned on
              providing one.
            </SubSection>
          </Section>

          {/* ── 2. How We Use Your Information ─────────────────────── */}
          <Section number="2" title="How We Use Your Information">
            <p>We use the information we collect to:</p>
            <BulletList items={[
              'Create and manage your account',
              'Process enrollment applications and payments',
              'Send invoices, receipts, payment confirmations, and program communications',
              'Respond to your questions and support requests',
              'Verify a student’s identity at check-in and pickup, using a headshot photo where one is on file',
              'Comply with legal obligations',
            ]} />
            <p>
              Student headshots are used only for this operational identity check inside the
              Portal. They are never used for publicity, marketing, social media, newsletters, or
              any other public purpose, and they are not used for facial recognition or any
              automated identification. Publicity photos are governed separately by the photo
              release consent in the student waiver, and that consent is neither required for nor
              affected by a pickup headshot.
            </p>
          </Section>

          {/* ── 3. Disclosure of Your Information ──────────────────── */}
          <Section number="3" title="Disclosure of Your Information">
            <SubSection title="3.1 — Third-Party Service Providers">
              We share your data only with the following third-party service providers, strictly
              for operating the Portal:
              <BulletList items={[
                'Stripe — payment processing (credit/debit card and ACH)',
                'Supabase — database hosting and authentication',
                'Google (Gmail API) — transactional email delivery',
              ]} />
              We do not sell your personal information or share it with any other third parties
              for marketing or commercial purposes.
            </SubSection>

            <SubSection title="3.2 — Who Can See Student Headshots">
              A student's headshot is visible only to our administrators and to the teachers and
              staff assigned to that student's programs, and only inside the Portal. It is not
              shown to other families, not visible on any public page, and not shared with any
              third party other than Supabase as our storage provider. Photos are stored in a
              private, non-public storage location and are loaded through short-lived links that
              expire, so a link cannot be forwarded or reused to view the photo later.
            </SubSection>

            <SubSection title="3.3 — Legal Requirements">
              We may disclose your information if required to do so by law or in response to
              valid legal process, or when we believe disclosure is necessary to protect the
              rights, property, or safety of Bright Minds Collective LLC, our users, or others.
            </SubSection>
          </Section>

          {/* ── 4. Security ─────────────────────────────────────────── */}
          <Section number="4" title="Security">
            <p>
              We use industry-standard security practices to protect your personal information,
              including encrypted connections (HTTPS) and access controls. No system is perfectly
              secure; we cannot guarantee that unauthorized third parties will never be able to
              defeat our measures.
            </p>
          </Section>

          {/* ── 5. Children's Privacy ───────────────────────────────── */}
          <Section number="5" title="Children's Privacy">
            <p>
              Our programs serve children of all ages, including children under 13. Account holders
              must be the parent or legal guardian of any student whose information they submit. We
              collect student information only with verifiable parental consent and solely for the
              purpose of administering our programs. This includes the headshot photo described in
              Sections 1.5 and 7.1, which we hold for the safety purpose of confirming a child is
              released to the right adult.
            </p>
            <p>
              Parents or legal guardians may review their child's information, request its
              deletion, or refuse further collection by contacting us. We do not condition a
              child's participation in any program on disclosure of more information than is
              reasonably necessary.
            </p>
          </Section>

          {/* ── 6. Your Data Rights ─────────────────────────────────── */}
          <Section number="6" title="Your Data Rights">
            <p>You have the right to:</p>
            <BulletList items={[
              'Access and update your account information',
              'Request a copy of the personal information we hold about you',
              'Request deletion of your personal information',
              'Decline a headshot photo for your child, or request removal of one already on file',
              'Object to the processing of your personal information',
            ]} />
            <p>
              To exercise these rights, contact us at{' '}
              <a href="mailto:billing@bhcskids.com" className="text-blue-700 hover:underline">
                billing@bhcskids.com
              </a>
              . We will investigate and respond within a reasonable time.
            </p>
          </Section>

          {/* ── 7. Data Retention ───────────────────────────────────── */}
          <Section number="7" title="Data Retention">
            <p>
              We retain account and transaction data for as long as your account is active and
              for a reasonable period thereafter to satisfy recordkeeping, legal, and accounting
              obligations. If you have questions about your data, contact us at{' '}
              <a href="mailto:billing@bhcskids.com" className="text-blue-700 hover:underline">
                billing@bhcskids.com
              </a>
              .
            </p>

            <SubSection title="7.1 — Student Headshot Photos">
              We keep a student's headshot only while it serves its purpose. A photo is deleted
              when the student is no longer enrolled in any of our programs — at the latest during our
              annual end-of-school-year review — and immediately whenever a parent or legal
              guardian asks us to remove it. Headshots are not retained for recordkeeping,
              accounting, or archival purposes, and a replacement photo removes the one it
              replaces.
            </SubSection>
          </Section>

          {/* ── 8. Changes to This Policy ───────────────────────────── */}
          <Section number="8" title="Changes to This Policy">
            <p>
              We may update this Privacy Policy at any time. For material changes, we will
              provide at least <strong>30 days' advance notice</strong> via email to your account
              address and/or a banner in the Portal before the new policy takes effect. Continued
              use of the Portal after an update constitutes your acceptance of the revised policy.
              Non-material changes (such as corrections or clarifications) take effect immediately
              upon posting.
            </p>
          </Section>

          {/* Contact */}
          <div className="mt-10 pt-8 border-t border-gray-200">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">Contact Us</h2>
            <div className="text-sm text-gray-600 space-y-1">
              <p className="font-medium text-gray-800">Bright Minds Collective LLC</p>
              <p>Operator of Bright Horizon Chinese School and Kaleido</p>
              <p>
                Email:{' '}
                <a href="mailto:billing@bhcskids.com" className="text-blue-700 hover:underline">
                  billing@bhcskids.com
                </a>
              </p>
              <p>Phone: 650-620-0299</p>
              <p>650 Shell Blvd, Foster City, CA 94404</p>
            </div>
          </div>

        </div>

        <p className="mt-6 text-center text-xs text-gray-400">
          &copy; {new Date().getFullYear()} Bright Minds Collective LLC. All rights reserved.
        </p>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
